import React from 'react';
import { motion } from 'framer-motion';
import MetricCard from '../components/MetricCard';
import InsightCard from '../components/InsightCard';
import Chart from '../components/Chart';
import ImplicitNeedsSection from '../components/ImplicitNeedsSection';
import PredictiveAnalytics from '../components/PredictiveAnalytics';
import * as FiIcons from 'react-icons/fi';

const { FiUsers, FiMessageCircle, FiTrendingUp, FiAlertTriangle, FiStar } = FiIcons;

const Dashboard = () => {
  const metrics = [
    {
      title: 'Total Feedback',
      value: '3,256',
      change: '12%',
      changeType: 'positive',
      icon: FiMessageCircle,
      color: 'primary',
      subtitle: 'Last 30 days'
    },
    {
      title: 'Sentiment Score',
      value: '8.4/10',
      change: '0.6',
      changeType: 'positive',
      icon: FiStar,
      color: 'success',
      subtitle: 'Overall satisfaction'
    },
    {
      title: 'Response Rate',
      value: '94%',
      change: '2%',
      changeType: 'positive',
      icon: FiTrendingUp,
      color: 'warning',
      subtitle: 'Support team'
    },
    {
      title: 'Critical Issues',
      value: '8',
      change: '3',
      changeType: 'negative',
      icon: FiAlertTriangle,
      color: 'danger',
      subtitle: 'Require attention'
    }
  ];

  const sentimentData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    series: [
      {
        name: 'Positive',
        type: 'line',
        data: [65, 68, 70, 72, 75, 78],
        smooth: true,
        lineStyle: { color: '#22c55e' },
        areaStyle: { color: 'rgba(34,197,94,0.1)' }
      },
      {
        name: 'Neutral',
        type: 'line',
        data: [25, 22, 20, 18, 17, 16],
        smooth: true,
        lineStyle: { color: '#f59e0b' },
        areaStyle: { color: 'rgba(245,158,11,0.1)' }
      },
      {
        name: 'Negative',
        type: 'line',
        data: [10, 10, 10, 10, 8, 6],
        smooth: true,
        lineStyle: { color: '#ef4444' },
        areaStyle: { color: 'rgba(239,68,68,0.1)' }
      }
    ]
  };

  const categoryData = {
    labels: ['Product', 'Service', 'Support', 'Billing', 'User Experience'],
    series: [
      {
        name: 'Feedback Count',
        type: 'bar',
        data: [
          { value: 856, itemStyle: { color: '#0ea5e9' } },
          { value: 745, itemStyle: { color: '#22c55e' } },
          { value: 652, itemStyle: { color: '#f59e0b' } },
          { value: 432, itemStyle: { color: '#ef4444' } },
          { value: 571, itemStyle: { color: '#8b5cf6' } }
        ]
      }
    ]
  };

  const insights = [
    {
      title: 'Rising Concern: Mobile App Performance',
      description: 'Users are reporting increased crashes on the mobile app after the latest update. Consider a hotfix for the navigation menu.',
      priority: 'high',
      category: 'Technical Issue',
      timestamp: '2 hours ago',
      icon: FiAlertTriangle,
      actions: [
        { label: 'View Details', onClick: () => console.log('View details') },
        { label: 'Create Task', onClick: () => console.log('Create task') }
      ]
    },
    {
      title: 'Customer Success Opportunity',
      description: 'Enterprise customers are requesting more training resources for the new analytics features. Consider creating dedicated tutorials.',
      priority: 'medium',
      category: 'Customer Success',
      timestamp: '1 day ago',
      icon: FiUsers,
      actions: [
        { label: 'Plan Training', onClick: () => console.log('Plan training') }
      ]
    }
  ];

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900">Dashboard</h1>
          <p className="text-secondary-600 mt-1">Customer feedback insights and trends</p>
        </div>
        <div className="flex items-center space-x-3">
          <select className="px-4 py-2 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500">
            <option>Last 7 days</option>
            <option>Last 30 days</option>
            <option>Last 90 days</option>
          </select>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors font-medium"
          >
            Generate Report
          </motion.button>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {metrics.map((metric, index) => (
          <MetricCard key={index} {...metric} />
        ))}
      </div>

      {/* Implicit Needs Analysis */}
      <ImplicitNeedsSection />

      {/* Predictive Analytics Section */}
      <PredictiveAnalytics />

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Chart title="Sentiment Trends Over Time" data={sentimentData} type="line" height={350} />
        <Chart title="Feedback by Category" data={categoryData} type="bar" height={350} />
      </div>

      {/* Key Insights */}
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-secondary-900">Key Insights</h2>
            <p className="text-secondary-600">AI-generated insights from customer feedback</p>
          </div>
          <button className="text-primary-600 hover:text-primary-700 font-medium text-sm">
            View All Insights
          </button>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {insights.map((insight, index) => (
            <InsightCard key={index} {...insight} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;